/**
 * Hosting Guide Page - step-by-step "how renting out your party supplies
 * works" content page (SEO Phase 2, docs/design/seo.md — owner-approved
 * copy 2026-07-03). Targets "rent out party supplies" query space.
 */

import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { withRouter } from 'react-router-dom';

import { logFunnelEvent } from '../../util/analytics';
import { Page, LayoutSingleColumn } from '../../components';
import { useConfiguration } from '../../context/configurationContext';
import { NamedLink } from '../../components';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';

import css from './HostingGuidePage.module.css';

// Step copy doubles as the HowTo structured data, so the schema can never
// drift from the visible content.
const STEPS = [
  {
    heading: 'Build your kit',
    body:
      'A kit is a complete party look: the theme, the tableware, the backdrop, whatever you would set out for the celebration. Kits rent better than single items because renters want the whole look without the scavenger hunt.',
  },
  {
    heading: "Photograph it like it's party day",
    body:
      'Set your kit up the way a renter would use it and shoot it in good light. Your first photo is your storefront. Kits with a styled hero shot get noticed.',
  },
  {
    heading: 'List it and set your price',
    body:
      "Describe what's included, piece by piece, and set a per-day price. You can offer tiers, like a basic version and a deluxe version of the same kit. Our team reviews each listing for quality before it goes live.",
  },
  {
    heading: 'Accept bookings',
    body:
      'When a renter requests your kit, you have 72 hours to accept, though most hosts respond much sooner. You can message the renter first if you have questions about their event.',
  },
  {
    heading: 'Hand it off',
    body:
      'Mark the kit ready, then meet for pickup or deliver it, whichever you offer. The renter confirms receipt in the app so there is never a question about who has the kit.',
  },
  {
    heading: 'Check it back in and get paid',
    body:
      "When the kit comes back, look it over and confirm the return. That confirmation starts your payout, which reaches your bank in 1 to 3 business days. If something came back damaged, report it with photos within 48 hours. The renter's deposit covers it first, and Merrily Damage Protection covers verified damage beyond that, up to 50% of the replacement cost.",
  },
];

export const HostingGuidePageComponent = props => {
  const { history, isAuthenticated } = props;
  const config = useConfiguration();

  useEffect(() => {
    logFunnelEvent('page_view_hosting_guide');
  }, []);

  // Same auth-aware CTA routing as VendorStarterGuidePage: logged-in hosts
  // go straight to the wizard, everyone else to signup.
  const handleCtaClick = () => {
    history.push(isAuthenticated ? '/l/new' : '/signup');
  };

  // Brand suffix appended centrally in Page.js (single + consistent).
  const title = 'How Renting Out Your Party Supplies Works';
  const description =
    'From first photo to first payout: the step-by-step guide to renting out your party supplies and themed kits on Merrily in the Santa Barbara area.';

  const schema = {
    '@context': 'http://schema.org',
    '@type': 'HowTo',
    name: 'How renting out your party supplies works',
    description,
    url: `${config.marketplaceRootURL}/become-a-vendor/how-it-works`,
    step: STEPS.map((s, i) => ({
      '@type': 'HowToStep',
      position: i + 1,
      name: s.heading,
      text: s.body,
    })),
  };

  return (
    <Page
      className={css.root}
      title={title}
      description={description}
      schema={schema}
      scrollingDisabled={false}
    >
      <LayoutSingleColumn topbar={<TopbarContainer />} footer={<FooterContainer />}>
        <main className={css.main}>
          <article className={css.container}>
            <h1 className={css.title}>How renting out your party supplies works</h1>
            <p className={css.intro}>
              From first photo to first payout, here is exactly what hosting on Merrily looks
              like.
            </p>

            {STEPS.map((s, i) => (
              <section key={s.heading} className={css.step}>
                <h2 className={css.stepHeading}>
                  {i + 1}. {s.heading}
                </h2>
                <p className={css.stepBody}>{s.body}</p>
              </section>
            ))}

            <div className={css.cta}>
              <button className={css.ctaButton} onClick={handleCtaClick}>
                List your first kit
              </button>
              <p className={css.crossLinks}>
                Wondering what to list?{' '}
                <NamedLink name="WhatToRentOutPage" className={css.inlineLink}>
                  See what party supplies rent well
                </NamedLink>
                , or head back to the{' '}
                <NamedLink name="VendorStarterGuidePage" className={css.inlineLink}>
                  Become a Host overview
                </NamedLink>
                .
              </p>
            </div>
          </article>
        </main>
      </LayoutSingleColumn>
    </Page>
  );
};

const mapStateToProps = state => ({
  isAuthenticated: !!state.auth?.isAuthenticated,
});

const HostingGuidePage = compose(
  withRouter,
  connect(mapStateToProps)
)(HostingGuidePageComponent);

export default HostingGuidePage;
